import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, File, X, CheckCircle } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';
import { useAuth } from '../../contexts/Authcontext';
import { careersAPI } from '../../services/api';

const JobApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: '',
    coverLetter: '',
    linkedinUrl: '',
    portfolioUrl: '',
    yearsExperience: 0,
  });

  const [resume, setResume] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { returnUrl: `/careers/job/${id}/apply` } });
      return;
    }
    fetchJobDetails();
  }, [id, user]);

  const fetchJobDetails = async () => {
    setLoading(true);
    try {
      // FIX: Matches getJobByID in services/api.js
      const data = await careersAPI.getJobByID(id);
      setJob(data);
    } catch (err) {
      console.error('Error fetching job:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'resume') {
        if (file.size > 10 * 1024 * 1024) {
          setError('Resume file size must be less than 10MB');
          return;
        }
        setResume(file);
      } else if (type === 'profilePicture') {
        if (file.size > 5 * 1024 * 1024) {
          setError('Profile picture must be less than 5MB');
          return;
        }
        setProfilePicture(file);
      }
      setError('');
    }
  };

  const removeFile = (type) => {
    if (type === 'resume') setResume(null);
    else if (type === 'profilePicture') setProfilePicture(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    if (!resume) {
      setError('Please upload your resume');
      setSubmitting(false);
      return;
    }

    try {
      // FIX: Use FormData for multipart/form-data support
      const data = new FormData();
      
      // FIX: Map frontend state keys to backend c.PostForm/c.FormFile keys
      data.append('first_name', formData.firstName);
      data.append('last_name', formData.lastName);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('years_experience', formData.yearsExperience);
      data.append('cover_letter', formData.coverLetter);
      data.append('linkedin_url', formData.linkedinUrl);
      data.append('portfolio_url', formData.portfolioUrl);
      
      // Files
      data.append('resume', resume);
      if (profilePicture) {
        data.append('profile_picture', profilePicture);
      }

      await careersAPI.applyForJob(id, data);
      setSuccess(true);

      setTimeout(() => {
        navigate('/my-applications');
      }, 3000);
    } catch (err) {
      console.error('Error submitting application:', err);
      setError(err.response?.data?.error || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        input: 'bg-white border-gray-300 text-gray-900 focus:border-blue-500',
        accent: 'from-blue-600 to-purple-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        input: 'bg-slate-900 border-slate-700 text-white focus:border-cyan-500',
        accent: 'from-cyan-600 to-blue-600',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      input: 'bg-slate-900/50 border-purple-500/30 text-white focus:border-purple-400',
      accent: 'from-purple-600 to-pink-600',
    };
  };

  const classes = getThemeClasses();

  if (loading) {
    return (
      <div className={`min-h-screen ${classes.bg} flex items-center justify-center pt-20`}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className={`mt-4 ${classes.subtext}`}>Loading application form...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`min-h-screen ${classes.bg} flex items-center justify-center pt-20`}>
        <div className={`${classes.cardBg} rounded-2xl p-8 border ${classes.cardBorder} max-w-md text-center`}>
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className={`text-2xl font-bold ${classes.text} mb-4`}>Application Submitted!</h2>
          <p className={`${classes.subtext} mb-6`}>
            Thank you for applying to {job?.title}. We'll review your profile and get back to you soon.
          </p>
          <p className={`text-sm ${classes.subtext}`}>Redirecting to your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${classes.bg} pt-20 pb-12`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to={`/careers/job/${id}`}
          className={`inline-flex items-center ${classes.subtext} hover:text-purple-400 mb-6 transition-colors`}
        >
          <ArrowLeft className="mr-2 w-5 h-5" />
          Back to job details
        </Link>

        <div className={`${classes.cardBg} rounded-2xl p-6 sm:p-8 border ${classes.cardBorder}`}>
          <h1 className={`text-3xl font-bold ${classes.text} mb-2`}>Apply for {job?.title}</h1>
          <p className={`${classes.subtext} mb-8`}>Fill out the form below to submit your application</p>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className={`text-xl font-bold ${classes.text} mb-4`}>Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>Resume * (PDF, DOC, DOCX - Max 10MB)</label>
              {!resume ? (
                <label className={`block w-full border-2 border-dashed ${classes.cardBorder} rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 transition-colors`}>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, 'resume')} className="hidden" />
                  <Upload className={`w-12 h-12 ${classes.subtext} mx-auto mb-3`} />
                  <p className={`${classes.text} font-medium mb-1`}>Click to upload resume</p>
                </label>
              ) : (
                <div className={`flex items-center justify-between p-4 border ${classes.cardBorder} rounded-lg`}>
                  <div className="flex items-center gap-3">
                    <File className={`w-8 h-8 ${classes.subtext}`} />
                    <div>
                      <p className={`${classes.text} font-medium`}>{resume.name}</p>
                      <p className={`${classes.subtext} text-sm`}>{(resume.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeFile('resume')} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"><X size={20} /></button>
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>Profile Picture (Optional)</label>
              {!profilePicture ? (
                <label className={`block w-full border-2 border-dashed ${classes.cardBorder} rounded-lg p-6 text-center cursor-pointer hover:border-purple-500 transition-colors`}>
                  <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'profilePicture')} className="hidden" />
                  <Upload className={`w-10 h-10 ${classes.subtext} mx-auto mb-2`} />
                  <p className={`${classes.text} text-sm`}>Click to upload photo</p>
                </label>
              ) : (
                <div className={`flex items-center justify-between p-4 border ${classes.cardBorder} rounded-lg`}>
                  <div className="flex items-center gap-3">
                    {profilePicture.type.startsWith('image/') && (
                      <img src={URL.createObjectURL(profilePicture)} alt="Preview" className="w-12 h-12 rounded-full object-cover" />
                    )}
                    <div>
                      <p className={`${classes.text} font-medium`}>{profilePicture.name}</p>
                      <p className={`${classes.subtext} text-sm`}>{(profilePicture.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeFile('profilePicture')} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"><X size={20} /></button>
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>Years of Experience *</label>
              <input
                type="number"
                name="yearsExperience"
                value={formData.yearsExperience}
                onChange={handleInputChange}
                required
                min="0"
                className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>Cover Letter *</label>
              <textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                required
                rows={6}
                className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
                placeholder="Tell us why you're a great fit..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>LinkedIn URL</label>
                <input
                  type="url"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>Portfolio URL</label>
                <input
                  type="url"
                  name="portfolioUrl"
                  value={formData.portfolioUrl}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2`}
                  placeholder="https://yourportfolio.com"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={submitting}
                className={`flex-1 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r ${classes.accent} text-white hover:shadow-lg transition-all disabled:opacity-50`}
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
              <Link
                to={`/careers/job/${id}`}
                className={`px-6 py-3 rounded-xl font-semibold border ${classes.cardBorder} ${classes.text} hover:bg-gray-700/20 transition-all`}
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobApplication;